/**
 * מאזן wizard — summary + payment. Shows NO results: every number (headline
 * summary + full opinion) is the paid product and the server does not send
 * any before payment. Here: readiness, the remaining gaps, the price
 * (+ coupon), the signed service agreement (required before paying), explicit
 * consent, and the CardCom reassurance modal.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, Lock, Tag } from 'lucide-react'
import PaymentReassuranceModal from '../../../components/payment/PaymentReassuranceModal'
import { handleApiError } from '../../../services/api'
import { CheckField } from './fields'
import ContractSection from './ContractSection'
import { ils, mazanApi, type MazanInputs, type PreviewResponse } from '../model'

interface Props {
  reportId: string
  inputs: MazanInputs
  /** saves pending wizard changes before preview / checkout */
  flush: () => Promise<void>
}

export default function SummaryStep({ reportId, inputs, flush }: Props) {
  const navigate = useNavigate()
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [coupon, setCoupon] = useState('')
  const [couponResult, setCouponResult] = useState<{ valid: boolean; reason?: string; discountedIls?: number; code?: string } | null>(null)
  const [dataConsent, setDataConsent] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [contractSigned, setContractSigned] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [working, setWorking] = useState(false)
  const [payError, setPayError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    flush()
      .then(() => mazanApi.preview(reportId))
      .then((p) => alive && setPreview(p))
      .catch((e) => alive && setLoadError(handleApiError(e)))
    return () => { alive = false }
  }, [reportId, flush])

  const name = (side: 'a' | 'b') => inputs.people[side === 'a' ? 0 : 1].name || (side === 'a' ? 'צד א׳' : 'צד ב׳')
  const price = couponResult?.valid && couponResult.discountedIls != null ? couponResult.discountedIls : preview?.priceIls ?? 0
  const canPay = !!preview && preview.valid && preview.blockers.length === 0 && (preview.priceIls > 0) && contractSigned && dataConsent && termsAccepted
  // 100% coupon: nothing to charge, so no CardCom page and no reassurance modal.
  const isFree = !!couponResult?.valid && price <= 0

  const applyCoupon = async () => {
    if (!coupon.trim()) return
    try {
      setCouponResult(await mazanApi.validateCoupon(reportId, coupon.trim()))
    } catch (e) {
      setCouponResult({ valid: false, reason: handleApiError(e) })
    }
  }

  const pay = async () => {
    setWorking(true)
    setPayError(null)
    try {
      await flush()
      const res = await mazanApi.checkout(reportId, {
        couponCode: couponResult?.valid ? couponResult.code : undefined,
        dataConsent: true,
        termsAccepted: true,
      })
      if (res.free) navigate(`/Mazan/result/${reportId}?status=success`)
      else if (res.url) window.location.assign(res.url)
    } catch (e) {
      setPayError(handleApiError(e))
      setModalOpen(false)
      setWorking(false)
    }
  }

  if (loadError) return <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">{loadError}</p>
  if (!preview) return <p role="status" className="text-gray-700">מחשב…</p>

  return (
    <div className="space-y-6">
      <section className="card border-t-4 border-emerald-800" aria-labelledby="mz-headline">
        <h3 id="mz-headline" className="text-base font-semibold text-gray-900">התחשיב מוכן — {name('a')} ו{name('b')}</h3>
        {preview.valid ? (
          <div className="mt-3 flex items-start gap-3 rounded-lg bg-amber-50 p-4 text-amber-950">
            <Lock size={20} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">התוצאות נעולות עד לתשלום</p>
              <p className="mt-1 text-sm">
                מיד לאחר התשלום יופק הדוח המלא:
                חוות דעת כלכלית חתומה, פסיקתא לכל קופה לפי חוק חלוקת חיסכון פנסיוני, טבלאות ההיוון והקיזוז, כ־PDF שישלח גם למייל.
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-gray-800">לא ניתן לחשב עדיין — יש להשלים את הפרטים החסרים.</p>
        )}
      </section>

      {preview.blockers.length > 0 && (
        <section role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-900" aria-label="פרטים חסרים">
          <p className="font-semibold">לפני התשלום יש להשלים:</p>
          <ul className="mt-2 list-inside list-disc">{preview.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
        </section>
      )}
      {preview.warnings.length > 0 && (
        <section className="rounded-lg bg-amber-50 p-4 text-sm text-amber-950" aria-label="הערות">
          <p className="font-semibold">פרטים שיופיעו בפסיקתא כ"חסר להשלמה" (אפשר לחזור ולהשלים):</p>
          <ul className="mt-2 list-inside list-disc">{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </section>
      )}

      {preview.valid && preview.blockers.length === 0 && (
        <ContractSection reportId={reportId} onSigned={setContractSigned} />
      )}

      <section className="card" aria-labelledby="mz-pay">
        <h3 id="mz-pay" className="text-base font-semibold text-gray-900">תשלום והפקת הדוח</h3>
        {preview.priceIls > 0 ? (
          <p className="mt-2 text-gray-800">
            מחיר הדוח: <strong className="text-lg">{ils.format(price)}</strong>
            {couponResult?.valid && <span className="ms-2 text-sm text-gray-600 line-through">{ils.format(preview.priceIls)}</span>}
            <span className="ms-2 text-sm text-gray-600">תשלום חד־פעמי, כולל מע״מ</span>
          </p>
        ) : (
          <p className="mt-2 text-amber-900">המחיר טרם הוגדר — לא ניתן לשלם כרגע. אנא פנו למשרד.</p>
        )}

        <div className="mt-4 flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="mz-coupon" className="mb-1 block text-sm font-medium text-gray-800">קוד קופון (לא חובה)</label>
            <input id="mz-coupon" className="input" dir="ltr" value={coupon} onChange={(e) => { setCoupon(e.target.value); setCouponResult(null) }}
              aria-describedby={couponResult ? 'mz-coupon-msg' : undefined} />
          </div>
          <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={applyCoupon}>
            <Tag size={16} aria-hidden="true" /> החלה
          </button>
        </div>
        {couponResult && (
          <p id="mz-coupon-msg" role="status" className={`mt-2 text-sm ${couponResult.valid ? 'text-emerald-900' : 'text-red-800'}`}>
            {!couponResult.valid
              ? couponResult.reason
              : isFree
                ? 'הקופון הוחל — הדוח ללא עלות, לא יידרשו פרטי תשלום.'
                : `הקופון הוחל — מחיר: ${ils.format(couponResult.discountedIls ?? price)}`}
          </p>
        )}

        <div className="mt-5 space-y-3">
          <CheckField id="mz-consent" checked={dataConsent} onChange={setDataConsent}
            label={<>אני מסכים/ה שהנתונים שהזנתי (כולל מספרי זהות, תאריכי לידה ויתרות פנסיוניות) יישמרו מוצפנים בשרת לצורך הפקת הדוח ושליחתו, כמפורט ב<Link to="/privacy" target="_blank" className="text-primary-700 underline">מדיניות הפרטיות</Link>.</>} />
          <CheckField id="mz-terms" checked={termsAccepted} onChange={setTermsAccepted}
            label={<>קראתי ואני מסכים/ה ל<Link to="/terms" target="_blank" className="text-primary-700 underline">תנאי השימוש</Link>, ומבין/ה שהדוח מבוסס על הנתונים שהזנתי, הוא טיוטה לפני הערות הצדדים, וכי מדובר במוצר דיגיטלי המופק מיד עם התשלום.</>} />
        </div>

        {payError && (
          <p role="alert" className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle size={16} aria-hidden="true" /> {payError}
          </p>
        )}

        <button type="button" className="btn btn-primary mt-5 w-full sm:w-auto" disabled={!canPay || working} aria-disabled={!canPay || working}
          onClick={() => (isFree ? pay() : setModalOpen(true))}>
          {isFree ? (working ? 'מפיק את הדוח…' : 'הפקת הדוח ללא תשלום') : 'לתשלום והפקת הדוח'}
        </button>
        {!canPay && preview.blockers.length === 0 && preview.priceIls > 0 && (
          <p className="mt-2 text-xs text-gray-600">
            {contractSigned ? 'יש לאשר את שתי ההצהרות כדי להמשיך.' : 'יש לחתום על הסכם השירות (למעלה) ולאשר את שתי ההצהרות כדי להמשיך.'}
          </p>
        )}
      </section>

      <PaymentReassuranceModal
        isOpen={modalOpen}
        planLabel="דוח איזון משאבים"
        amountIls={price}
        priceSuffix="₪ · תשלום חד־פעמי"
        working={working}
        onConfirm={pay}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
